import { ColumnApi, ColumnState } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnOrder } from './types';

interface UseColumnVisibleProps {
  allColumns: ColumnOrder[];
  isAllColumnsVisible: boolean;
  toggleSingleColumnVisible: (colId: string, visible: boolean) => void;
  toggleAllColumnsVisible: () => void;
  resetDefault: () => void;
}

export const useColumnVisible = (
  columnOrder: ColumnOrder[],
  excludedColumns: string[],
  defaultColumnOrder: string[],
  gridColumnApi?: ColumnApi,
): UseColumnVisibleProps => {
  const [isAllColumnsVisible, setIsAllColumnsVisible] = useState(true);

  useEffect(() => {
    if (
      columnOrder.length > 0 &&
      columnOrder.some((x) => x.hide === true) &&
      isAllColumnsVisible
    ) {
      setIsAllColumnsVisible(false);
    } else if (
      columnOrder.length > 0 &&
      columnOrder.every((x) => x.hide === false) &&
      !isAllColumnsVisible
    ) {
      setIsAllColumnsVisible(true);
    }
  }, [columnOrder, isAllColumnsVisible]);

  const allColumns = useMemo(
    () => columnOrder.filter((col) => !excludedColumns.includes(col.colId!)),
    [columnOrder, excludedColumns],
  );

  const toggleSingleColumnVisible = (colId: string, visible: boolean) => {
    gridColumnApi?.setColumnsVisible([colId], visible);
  };

  const toggleAllColumnsVisible = () => {
    if (!allColumns) {
      return;
    }
    gridColumnApi?.setColumnsVisible(
      allColumns.map((x) => x.colId as string),
      !isAllColumnsVisible,
    );
    setIsAllColumnsVisible(!isAllColumnsVisible);
  };

  const relativeSortColumns = useCallback((): ColumnState[] => {
    const order = Object.fromEntries(defaultColumnOrder.map((v, i) => [v, i]));

    return Array.from(allColumns)
      .sort(
        (a, b) =>
          (order[a.colId!] ?? Number.MAX_VALUE) -
          (order[b.colId!] ?? Number.MAX_VALUE),
      )
      .map((x) => ({ ...x, hide: false }));
  }, [allColumns, defaultColumnOrder]);

  const resetDefault = () => {
    gridColumnApi?.setState(relativeSortColumns());
    setIsAllColumnsVisible(true);
  };

  return {
    allColumns,
    isAllColumnsVisible,
    toggleSingleColumnVisible,
    toggleAllColumnsVisible,
    resetDefault,
  };
};
